import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (amount, currency) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase(), minimumFractionDigits: 0 }).format((amount || 0) / 100);

const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b-2 border-[#0A0A0A]">
    <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="/" className="font-display font-extrabold text-xl tracking-tighter" data-testid="brand-logo">
        LEDGER<span className="text-[#FF3B30]">/</span>OS
      </a>
      <div className="flex items-center gap-6 font-mono-b text-xs uppercase tracking-[0.2em]">
        <a href="#pricing" className="hidden sm:inline hover:text-[#FF3B30] transition-colors duration-200" data-testid="nav-pricing">Pricing</a>
        <a href="#pricing" className="bg-[#0A0A0A] text-white px-4 py-2 border-2 border-[#0A0A0A] hover:bg-[#FF3B30] hover:border-[#FF3B30] transition-colors duration-200" data-testid="nav-cta">Buy Now</a>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="max-w-6xl mx-auto px-6 pt-32 pb-16 grid md:grid-cols-12 gap-8 items-start">
    <div className="md:col-span-7">
      <div className="font-mono-b text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-6">
        [ ONE-TIME PAYMENT · LIFETIME ACCESS ]
      </div>
      <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tighter">
        Ship faster.<br />Pay once.<br /><span className="text-[#FF3B30]">Own it forever.</span>
      </h1>
      <p className="mt-8 text-base sm:text-lg max-w-md leading-relaxed text-[#0A0A0A]/80">
        A no-nonsense toolkit for builders. Pick a plan, check out securely via Stripe,
        and unlock your workspace the moment payment clears.
      </p>
      <a href="#pricing" className="inline-flex items-center gap-2 mt-10 bg-[#FF3B30] text-white font-mono-b uppercase tracking-[0.15em] text-sm px-8 py-4 border-2 border-[#0A0A0A] brutal-shadow hover:-translate-y-1 transition-transform duration-200" data-testid="hero-cta">
        View Plans <ArrowUpRight size={18} strokeWidth={2.5} />
      </a>
    </div>
    <div className="md:col-span-5">
      <div className="border-2 border-[#0A0A0A] brutal-shadow overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
          alt="Abstract architectural geometry"
          className="w-full h-72 object-cover grayscale"
        />
        <div className="border-t-2 border-[#0A0A0A] px-5 py-3 font-mono-b text-xs uppercase tracking-[0.2em] flex justify-between">
          <span>SECURE_CHECKOUT</span><span className="text-[#FF3B30]">● STRIPE</span>
        </div>
      </div>
    </div>
  </section>
);

const PricingCard = ({ product, index, onBuy, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
    className={`relative bg-white border-2 p-8 flex flex-col ${
      product.recommended
        ? "border-[#FF3B30] brutal-shadow-red md:-translate-y-4 md:mb-[-1rem]"
        : "border-[#0A0A0A] brutal-shadow"
    }`}
    data-testid={`pricing-card-${product.id}`}
  >
    {product.recommended && (
      <div className="absolute -top-3 left-8 bg-[#FFD600] border-2 border-[#0A0A0A] px-3 py-1 font-mono-b text-[10px] uppercase tracking-[0.2em]">
        Most Popular
      </div>
    )}
    <div className="font-mono-b text-xs uppercase tracking-[0.25em] text-[#0A0A0A]/60">{product.name}</div>
    <div className="mt-4 flex items-end gap-2">
      <span className="font-display font-extrabold text-5xl tracking-tighter">{fmt(product.amount, product.currency)}</span>
      <span className="font-mono-b text-xs uppercase text-[#0A0A0A]/50 mb-2">/ once</span>
    </div>
    <p className="mt-4 text-sm leading-relaxed text-[#0A0A0A]/70">{product.description}</p>
    <ul className="mt-6 space-y-3 flex-1">
      {product.features.map((f) => (
        <li key={f} className="flex items-start gap-3 text-sm">
          <span className={`mt-0.5 flex-shrink-0 w-4 h-4 border-2 border-[#0A0A0A] flex items-center justify-center ${product.recommended ? "bg-[#FF3B30]" : "bg-white"}`}>
            <Check size={11} strokeWidth={3} className={product.recommended ? "text-white" : "text-[#0A0A0A]"} />
          </span>
          {f}
        </li>
      ))}
    </ul>
    <button
      onClick={() => onBuy(product)}
      disabled={loading}
      className={`mt-8 w-full font-mono-b uppercase tracking-[0.15em] text-sm px-6 py-4 border-2 border-[#0A0A0A] transition-transform duration-200 hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2 ${
        product.recommended ? "bg-[#FF3B30] text-white" : "bg-[#0A0A0A] text-white"
      }`}
      data-testid={`buy-button-${product.id}`}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} strokeWidth={2.5} />}
      Buy {product.name}
    </button>
  </motion.div>
);

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/products`)
      .then((r) => setProducts(r.data.products))
      .catch(() => toast.error("Failed to load products"));
  }, []);

  const openBuy = (product) => {
    setSelected(product);
    setDialogOpen(true);
  };

  const startCheckout = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: selected.lookup_key,
        origin_url: window.location.origin,
        email,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <Hero />

      <section id="pricing" className="max-w-6xl mx-auto px-6 py-20">
        <div className="border-t-2 border-[#0A0A0A] pt-10 mb-14">
          <div className="font-mono-b text-xs uppercase tracking-[0.3em] text-[#FF3B30]">[ PRICING ]</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-3">
            Choose your plan
          </h2>
          <p className="mt-4 text-base text-[#0A0A0A]/70 max-w-lg leading-relaxed">
            Prices are enforced server-side. You just pick a plan — Stripe handles the rest securely.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((p, i) => (
            <PricingCard key={p.id} product={p} index={i} onBuy={openBuy} loading={submitting && selected?.id === p.id} />
          ))}
        </div>
      </section>

      <footer className="border-t-2 border-[#0A0A0A] mt-10">
        <div className="max-w-6xl mx-auto px-6 py-8 font-mono-b text-xs uppercase tracking-[0.2em] flex flex-col sm:flex-row justify-between gap-2 text-[#0A0A0A]/60">
          <span>© {new Date().getFullYear()} LEDGER/OS</span>
          <span>Payments secured by Stripe · Test mode</span>
        </div>
      </footer>

      <Dialog open={dialogOpen} onOpenChange={(o) => !submitting && setDialogOpen(o)}>
        <DialogContent className="rounded-none border-2 border-[#0A0A0A] brutal-shadow sm:max-w-md" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl tracking-tighter">
              Checkout — {selected?.name}
            </DialogTitle>
            <DialogDescription className="font-mono-b text-xs uppercase tracking-[0.15em] text-[#0A0A0A]/60">
              {selected && fmt(selected.amount, selected.currency)} · One-time payment
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <label className="font-mono-b text-xs uppercase tracking-[0.2em] block mb-2">Email for receipt</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border-2 border-[#0A0A0A] px-4 py-3 font-mono-b text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF3B30]"
              data-testid="checkout-email-input"
              disabled={submitting}
            />
            <button
              onClick={startCheckout}
              disabled={submitting}
              className="mt-5 w-full bg-[#FF3B30] text-white font-mono-b uppercase tracking-[0.15em] text-sm px-6 py-4 border-2 border-[#0A0A0A] brutal-shadow-sm hover:-translate-y-1 transition-transform duration-200 disabled:opacity-60 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
              data-testid="proceed-to-stripe-button"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} strokeWidth={2.5} />}
              {submitting ? "Redirecting..." : "Proceed to Stripe"}
            </button>
            <p className="mt-4 font-mono-b text-[10px] uppercase tracking-[0.15em] text-[#0A0A0A]/50 leading-relaxed">
              Test card: 4242 4242 4242 4242 · any future date · any CVC
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
